import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountCircle,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Logout,
  Menu as MenuIcon,
  Notifications,
  SettingsOutlined,
} from '@mui/icons-material';
import * as icons from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import EasyStaffLogo from 'src/assets/images/easystaff_logo-header.png';
import ChatNotificationBadge from 'src/modules/chat/components/chatNotificationBadge';
import { NotificationList } from 'src/modules/notifications/components/notificationList';
import { useGlobals } from 'src/services/contexts/appContext';
import userManager from 'src/services/identity/userManager';
import { RootStateType } from 'src/services/redux/reducers';
import { RoutesEnum } from 'src/shared/enums/routes.enum';
import { UserTypeEnum } from 'src/shared/enums/userType.enum';
import { usePersistedBoolean } from 'src/services/helpers/usePersistedBoolean';
import { parseCalendarSection } from 'src/modules/calendar/helpers/calendarSection';
import { getRouteForModule, ModuleDisplayName } from './helper/modulesHelper';
import { Colors } from './styles/colors';
import { getSidebarItemButtonSx, SIDEBAR_COMPACT_QUERY, SIDEBAR_ITEM_SX } from './styles/sidebar';
import { SwitchLanguageButton } from './switchLanguageButton';

const DRAWER_WIDTH_EXPANDED = 272;
const DRAWER_WIDTH_COLLAPSED = 84;
const SIDEBAR_COLLAPSED_KEY = 'ui.sidebarCollapsed';
const DESKTOP_GUTTER = 16;
const DESKTOP_GUTTER_COMPACT = 8;
const UTILITY_TOP = 16;
const UTILITY_BUTTON_SX = {
  bgcolor: '#FFFFFF',
  boxShadow: '0 2px 6px rgba(10, 18, 25, 0.03)',
  border: 'none',
  '&:hover': {
    bgcolor: Colors.GRAY_FAIR,
  },
} as const;

const capitalizeFirstLetter = (value?: string): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const isActiveRoute = (currentPath: string, targetPath: string) => {
  if (targetPath === RoutesEnum.Start) return currentPath === RoutesEnum.Start;
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

const AppShell = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { modules } = useSelector((state: RootStateType) => state.appState);
  const { loggedInUser, setShowChatModal, showChatModal, unreadNotifications } = useGlobals();

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = usePersistedBoolean(SIDEBAR_COLLAPSED_KEY);
  const [anchorNoticeElement, setAnchorNoticeElement] = useState<null | HTMLElement>(null);

  const openNotification = Boolean(anchorNoticeElement);

  const isCalendarActive = isActiveRoute(location.pathname, RoutesEnum.Calendar);
  const calendarSection = parseCalendarSection(new URLSearchParams(location.search).get('section'));
  const [isCalendarExpanded, setIsCalendarExpanded] = useState<boolean>(isCalendarActive);

  useEffect(() => {
    setIsCalendarExpanded(isCalendarActive);
  }, [isCalendarActive]);

  let drawerWidth = 0;
  if (isDesktop) {
    drawerWidth = isSidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
  }

  const dynamicModules = useMemo(() => modules?.filter((item) => item.Title !== 'Chat') ?? [], [modules]);

  const handleToggleSidebar = () => {
    if (!isDesktop) {
      setMobileDrawerOpen((prev) => !prev);
      return;
    }

    setIsSidebarCollapsed((prev) => !prev);
  };

  const renderMenuItem = ({
    label,
    to,
    icon,
    active,
    selectedBg,
    onClick,
    trailing,
    dense,
  }: {
    label: React.ReactNode;
    to?: string;
    icon?: React.ReactNode;
    active?: boolean;
    selectedBg?: boolean;
    onClick?: () => void;
    trailing?: React.ReactNode;
    dense?: boolean;
  }) => {
    const buttonProps = to ? { component: Link as any, to } : {};

    return (
      <ListItem disablePadding sx={SIDEBAR_ITEM_SX}>
        <Tooltip title={isSidebarCollapsed && isDesktop && typeof label === 'string' ? label : ''} placement='right'>
          <ListItemButton
            {...buttonProps}
            onClick={onClick}
            selected={selectedBg ?? active}
            sx={{
              ...getSidebarItemButtonSx(dense),
              borderRadius: 2,
              justifyContent: isSidebarCollapsed && isDesktop ? 'center' : 'flex-start',
              px: isSidebarCollapsed && isDesktop ? 1.5 : 2,
              '&.Mui-selected': {
                bgcolor: Colors.GRAY_FAIR,
              },
              '&.Mui-selected:hover': {
                bgcolor: Colors.GRAY_FAIR,
              },
            }}
          >
            {icon ? (
              <ListItemIcon
                sx={{
                  minWidth: isSidebarCollapsed && isDesktop ? 0 : 36,
                  justifyContent: 'center',
                  color: active ? Colors.ACCENT_DARK : Colors.BLACK,
                }}
              >
                {icon}
              </ListItemIcon>
            ) : null}
            {!isSidebarCollapsed || !isDesktop ? (
              <ListItemText primary={label} primaryTypographyProps={active ? { fontWeight: 600, color: Colors.ACCENT_DARK } : undefined} />
            ) : null}
            {trailing && (!isSidebarCollapsed || !isDesktop) ? trailing : null}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        [SIDEBAR_COMPACT_QUERY]: { p: 1.5 },
      }}
    >
      <Box
        sx={{
          pt: 1,
          pb: 2,
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
          [SIDEBAR_COMPACT_QUERY]: { pt: 0.5, pb: 2 },
        }}
      >
        <Box component={Link} to={RoutesEnum.Start} sx={{ display: 'flex' }} onClick={() => setMobileDrawerOpen(false)}>
          <Box
            component='img'
            src={EasyStaffLogo}
            srcSet={EasyStaffLogo}
            sx={{ height: isSidebarCollapsed && isDesktop ? 28 : 40, objectFit: 'contain', [SIDEBAR_COMPACT_QUERY]: { height: 28 } }}
            alt='logo-company'
            loading='lazy'
          />
        </Box>
      </Box>

      {/* Bara menyvalen scrollar, så att loggan och Min profil, språk och Logga ut alltid syns. */}
      <List component='nav' disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin' }}>
        {dynamicModules.map((item) => {
          const iconKey = capitalizeFirstLetter(item.WebIcon) as keyof typeof icons;
          const IconComponent = icons[iconKey] || icons.ErrorOutline;
          const targetPath = getRouteForModule(item.Title);
          const active = isActiveRoute(location.pathname, targetPath);

          if (item.Title === 'Calendar') {
            const showSubItems = isCalendarExpanded && (!isSidebarCollapsed || !isDesktop);
            const isAbsenceActive = isCalendarActive && calendarSection === 'absence';
            const isBookingsActive = isCalendarActive && calendarSection === 'bookings';

            return (
              <Box key={item.Title}>
                {renderMenuItem({
                  label: <ModuleDisplayName moduleTitle={item.Title} />,
                  to: targetPath,
                  icon: <IconComponent />,
                  active,
                  onClick: () => setMobileDrawerOpen(false),
                  trailing: (
                    <IconButton
                      size='small'
                      aria-label={isCalendarExpanded ? 'collapse' : 'expand'}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsCalendarExpanded((prev) => !prev);
                      }}
                      sx={{ ml: 'auto', color: 'inherit' }}
                    >
                      {isCalendarExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
                    </IconButton>
                  ),
                })}

                <Collapse in={showSubItems} timeout='auto' unmountOnExit>
                  <List component='div' disablePadding sx={{ pl: 3 }}>
                    {renderMenuItem({
                      label: t('booking:sectionAbsence'),
                      to: `${targetPath}?section=absence`,
                      active: isAbsenceActive,
                      selectedBg: false,
                      onClick: () => setMobileDrawerOpen(false),
                      dense: true,
                    })}
                    {renderMenuItem({
                      label: t('booking:sectionBooking'),
                      to: `${targetPath}?section=bookings`,
                      active: isBookingsActive,
                      selectedBg: false,
                      onClick: () => setMobileDrawerOpen(false),
                      dense: true,
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          return renderMenuItem({
            label: <ModuleDisplayName moduleTitle={item.Title} />,
            to: targetPath,
            icon: <IconComponent />,
            active,
            onClick: () => setMobileDrawerOpen(false),
          });
        })}

        {loggedInUser?.UserType === UserTypeEnum.Admin
          ? renderMenuItem({
              label: 'Admin',
              to: RoutesEnum.Admin,
              icon: <SettingsOutlined />,
              active: isActiveRoute(location.pathname, RoutesEnum.Admin),
              onClick: () => setMobileDrawerOpen(false),
            })
          : null}
      </List>

      <Box
        sx={{
          mt: 1,
          pt: 1.5,
          borderTop: `1px solid rgba(10, 18, 25, 0.08)`,
          bgcolor: 'transparent',
          px: 0,
          pb: 0.5,
          flexShrink: 0,
          [SIDEBAR_COMPACT_QUERY]: { mt: 0.5, pt: 1, pb: 0 },
        }}
      >
        {renderMenuItem({
          label: t('profile'),
          to: RoutesEnum.Profile,
          icon: <AccountCircle />,
          active: isActiveRoute(location.pathname, RoutesEnum.Profile),
          onClick: () => setMobileDrawerOpen(false),
        })}

        <SwitchLanguageButton variant='listItem' collapsed={isSidebarCollapsed && isDesktop} />

        {renderMenuItem({
          label: t('common:logout'),
          icon: <Logout />,
          onClick: () => userManager.signoutRedirect(),
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: Colors.BACKGROUND }}>
      <Box
        sx={{
          position: 'fixed',
          top: UTILITY_TOP,
          right: { xs: 12, md: 18 },
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {isDesktop ? (
          <IconButton
            aria-label='toggle sidebar'
            onClick={handleToggleSidebar}
            sx={{
              position: 'fixed',
              top: UTILITY_TOP,
              left: `${drawerWidth + DESKTOP_GUTTER * 2 + 8}px`,
              ...UTILITY_BUTTON_SX,
              zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
            }}
          >
            {isSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        ) : null}

        {!isDesktop ? (
          <IconButton aria-label='open sidebar' onClick={handleToggleSidebar} sx={UTILITY_BUTTON_SX}>
            <MenuIcon />
          </IconButton>
        ) : null}

        <IconButton
          color='primary'
          aria-label='chat'
          sx={{ ...UTILITY_BUTTON_SX, color: Colors.BLACK }}
          onClick={() => setShowChatModal(!showChatModal)}
        >
          <ChatNotificationBadge />
        </IconButton>

        <IconButton
          id='notification-menu'
          aria-label={t('common:notifications')}
          aria-controls={openNotification ? 'notification-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={openNotification ? 'true' : undefined}
          onClick={(event) => setAnchorNoticeElement(event.currentTarget)}
          sx={{ ...UTILITY_BUTTON_SX, color: Colors.BLACK }}
        >
          <Badge badgeContent={unreadNotifications?.length} color='warning' max={99} overlap='circular'>
            <Notifications sx={{ fontSize: 24 }} />
          </Badge>
        </IconButton>

        <Menu
          id='notification-menu'
          anchorEl={anchorNoticeElement}
          open={openNotification}
          onClose={() => setAnchorNoticeElement(null)}
          PaperProps={{
            sx: {
              width: { xs: '88%', md: 560 },
              maxWidth: 'calc(100vw - 24px)',
              height: { xs: 'calc(100vh - 72px)', lg: 'calc(100vh - 32px)' },
              mt: { xs: 7, lg: 2 },
              mr: { xs: 1, lg: 2 },
              borderRadius: { xs: 3, lg: 4 },
              bgcolor: '#FFFFFF',
              border: 'none',
              boxShadow: '0 4px 10px rgba(10, 18, 25, 0.045)',
              overflow: 'auto',
            },
          }}
        >
          <NotificationList />
        </Menu>
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: isDesktop ? drawerWidth + DESKTOP_GUTTER * 2 : DRAWER_WIDTH_EXPANDED,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: isDesktop ? drawerWidth : DRAWER_WIDTH_EXPANDED,
              top: isDesktop ? `${DESKTOP_GUTTER}px` : '0px',
              left: isDesktop ? `${DESKTOP_GUTTER}px` : '0px',
              height: isDesktop ? `calc(100% - ${DESKTOP_GUTTER * 2}px)` : '100%',
              boxSizing: 'border-box',
              border: 'none',
              borderRadius: isDesktop ? 4 : 0,
              bgcolor: '#FFFFFF',
              boxShadow: isDesktop ? '0 4px 10px rgba(10, 18, 25, 0.045)' : 'none',
              overflow: 'hidden',
              [SIDEBAR_COMPACT_QUERY]: isDesktop
                ? { top: `${DESKTOP_GUTTER_COMPACT}px`, height: `calc(100% - ${DESKTOP_GUTTER_COMPACT * 2}px)` }
                : undefined,
            },
          }}
        >
          {sidebarContent}
        </Drawer>

        <Box
          component='main'
          sx={{
            flexGrow: 1,
            minWidth: 0,
            mt: 9,
            px: { xs: 1, md: 2 },
            pb: 2,
            transition: theme.transitions.create(['margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
